import { NgxSliderModule } from '@angular-slider/ngx-slider'
import { CommonModule } from '@angular/common'
import { AfterViewInit, Component, Inject } from '@angular/core'
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog'
import { ComponentModifier } from '@app/features/company-management/models/component-modifier.model'
import { CompanyManagementSelectors } from '@app/features/company-management/store/selectors/company-management.selectors'
import { ScenarioCategory } from '@app/features/scope-overview/model/scenario-category.model'
import { ScenarioQuestion } from '@app/features/scope-overview/model/scenario-question.model'
import { Scenario } from '@app/features/scope-overview/model/scenario.model'
import { ScopeDynamicFieldSettingModel } from '@app/features/scoping/models/scope-dynamic-field-setting.model'
import { ScopeUiAutocompleteComponent } from '@app/shared/components/ui-components/scope-ui-autocomplete/scope-ui-autocomplete.component'
import { ScopeUiDatepickerComponent } from '@app/shared/components/ui-components/scope-ui-datepicker/scope-ui-datepicker.component'
import { SharedModule } from '@app/shared/shared.module'
import { CKEditorModule } from '@ckeditor/ckeditor5-angular'
import { QuestionType } from '@core/model/enums/question-type.enum'
import { Store } from '@ngrx/store'
import { FormulaBuilderComponent } from '@shared/components/formula-builder/formula-builder.component'
import { ScopeUiCheckboxComponent } from '@shared/components/ui-components/scope-ui-checkbox/scope-ui-checkbox.component'
import { ScopeUiChipsComponent } from '@shared/components/ui-components/scope-ui-chips/scope-ui-chips.component'
import { ScopeUiDropdownComponent } from '@shared/components/ui-components/scope-ui-dropdown/scope-ui-dropdown.component'
import { ScopeUiInputComponent } from '@shared/components/ui-components/scope-ui-input/scope-ui-input.component'
import { ScopeUiModalComponent } from '@shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { FormulaService } from '@shared/services/formula.service'
import { generateFieldId } from '@shared/utils/utils'
import { plainToInstance } from 'class-transformer'
import { isEqual } from 'lodash'
import { NgxMaskDirective } from 'ngx-mask'
import { Observable } from 'rxjs'

export interface AddEditQuestionModalConfig {
  type: QuestionType
  question?: ScenarioQuestion
  category: ScenarioCategory
  scenario: Scenario
  dynamicFields: ScopeDynamicFieldSettingModel[]
  modifiers: ComponentModifier[]
  confirmCallback: (question: any, fieldIDControl: FormControl) => void
}

@Component({
  selector: 'add-edit-question-modal',
  standalone: true,
  imports: [
    CommonModule,
    ScopeUiDatepickerComponent,
    ScopeUiAutocompleteComponent,
    SharedModule,
    MatDatepickerModule,
    CKEditorModule,
    ScopeUiInputComponent,
    NgxSliderModule,
    NgxMaskDirective,
    ScopeUiDropdownComponent,
    FormulaBuilderComponent,
    ScopeUiChipsComponent,
    ScopeUiCheckboxComponent,
  ],
  templateUrl: './add-edit-question-modal.component.html',
  styleUrls: ['./add-edit-question-modal.component.scss'],
})
export class AddEditQuestionModalComponent implements AfterViewInit {
  updateLoading$: Observable<boolean> = this.store.select(
    CompanyManagementSelectors.selectLoadingAddUpdateScenarioQuestion
  )

  title: string
  instructions: string
  questionTextLabel: string
  configureTitle: string = 'Configure answer field'

  scenario: Scenario
  question: ScenarioQuestion = new ScenarioQuestion()
  options: string[] = []
  showAlways: boolean = true
  showOnCondition: boolean = false

  fieldIDControl = new FormControl('')
  minimumControl = new FormControl()
  maximumControl = new FormControl()
  defaultValueControl = new FormControl()
  formulaControl = new FormControl()
  conditionControl = new FormControl()
  optionsListControl = new FormControl([], [Validators.required, this.optionsListValidator()])

  private showDependencyWarning: boolean = true

  readonly MAX_NUMBER_LENGTH = 19

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AddEditQuestionModalConfig,
    private store: Store,
    public dialog: MatDialog,
    private formulaService: FormulaService
  ) {
    let categories = data.scenario.categories.filter((c) => c.orderIndex < data.category.orderIndex)
    let questions = data.question
      ? data.category.questions.filter((q) => q.orderIndex < data.question.orderIndex)
      : data.category.questions
    categories.push({ ...data.category, questions })
    this.scenario = { ...data.scenario, categories }

    if (data.question) this.setQuestionValues()

    this.setTitles()
  }

  ngAfterViewInit() {
    if (this.question) {
      this.fieldIDControl.setValue(this.question.fieldId)
      this.fieldIDControl.updateValueAndValidity()
      this.fieldIDControl.disable()
      this.minimumControl.setValue(this.question.minimum)
      this.maximumControl.setValue(this.question.maximum)
      this.defaultValueControl.setValue(this.question.defaultValue)
      this.optionsListControl.setValue(this.options)
      this.optionsListControl.updateValueAndValidity()
    }
  }

  isNumber = (() => this.data.type === QuestionType.NUMBER)()
  isText = (() => this.data.type === QuestionType.TEXT)()
  isDropdown = (() => this.data.type === QuestionType.DROPDOWN)()
  isDate = (() => this.data.type === QuestionType.DATE)()
  isFormula = (() => this.data.type === QuestionType.FORMULA)()
  decimalPlaces = (() => (this.question.wholeNumber ? 0 : 2))()

  optionsListValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null =>
      control.value?.find((o) => o.includes(',')) ? { custom: 'Option cannot contain a comma' } : null
  }

  disableSubmit(): boolean {
    return (
      !this.question.displayText ||
      !this.hasChanges(this.data.question) ||
      !this.isFieldIdValid() ||
      !this.isShowOnConditionValid() ||
      !this.isFormulaValid() ||
      !this.isDropdownValid() ||
      !this.isNumberValid()
    )
  }

  isFieldIdValid = () => this.question.fieldId && (this.fieldIDControl.valid || this.fieldIDControl.disabled)

  isShowOnConditionValid = () =>
    !this.showOnCondition || (this.question.displayCondition && this.conditionControl.valid)

  isFormulaValid = () => !this.isFormula || (this.question.formula && this.formulaControl.valid)

  isDropdownValid = () => !this.isDropdown || (this.options.length && this.optionsListControl.valid)

  isNumberValid = () =>
    !this.isNumber || (this.minimumControl.valid && this.maximumControl.valid && this.defaultValueControl.valid)

  hasChanges = (question: ScenarioQuestion) =>
    !question ||
    !isEqual(this.question, question) ||
    this.showOnCondition != !!question.displayCondition ||
    this.options.join(',') != question.optionsList

  submit() {
    this.data.confirmCallback(
      {
        ...this.data.question,
        ...this.question,
        type: this.data.type,
        optionsList: this.options.join(','),
        displayCondition: this.showOnCondition ? this.question.displayCondition : null,
      },
      this.fieldIDControl
    )
  }

  setFieldLabel(fieldLabel: string) {
    this.question.fieldLabel = fieldLabel
    if (!this.data.question && !this.fieldIDControl.dirty) {
      let fieldId = generateFieldId(fieldLabel)
      this.fieldIDControl.setValue(fieldId)
      this.setFieldID(fieldId)
    }
  }

  setFieldID(fieldId: string) {
    this.question.fieldId = fieldId
    if (this.data.scenario.categories.flatMap((c) => c.questions).find((q) => q.fieldId === this.question.fieldId)) {
      this.fieldIDControl.setErrors({ conflict: true })
    } else if (!this.formulaService.isItPossibleToAddFieldID(fieldId)) {
      this.fieldIDControl.setErrors({ custom: 'Not a valid Field ID' })
    } else {
      this.fieldIDControl.setErrors(null)
    }
    this.fieldIDControl.markAsTouched()
  }

  checkDependencies() {
    if (this.showDependencyWarning && this.data.question) {
      this.showDependencyWarning = false
      let { questionDependencies, modifierDependencies } = this.formulaService.getReferencedFieldsForQuestion(
        this.question.fieldId,
        this.data.scenario,
        this.data.modifiers
      )

      if (questionDependencies.length || modifierDependencies.length) {
        let modalConfig = {
          title: `Question Has Dependencies`,
          body:
            `By changing the condition you can potentially break the rules in the following calculations:\n\n` +
            (questionDependencies.length
              ? 'Questions:\n' +
                questionDependencies.map((d) => `\u2022 ${d.categoryName} > ${d.displayText}\n`).join('')
              : '') +
            (modifierDependencies.length
              ? 'Modifiers:\n' + modifierDependencies.map((d) => `\u2022 ${d.name}\n`).join('')
              : ''),
          confirmText: 'Ok',
          confirmCallback: () => {
            dialog.close()
          },
          cancelCallback: undefined,
          inputs: [],
          isMemberModal: undefined,
          limitBodyWidth: true,
        }
        let dialog = this.dialog.open(ScopeUiModalComponent, { data: modalConfig })
      }
    }
  }

  private setQuestionValues() {
    this.question = plainToInstance(ScenarioQuestion, this.data.question)
    this.options = this.data.question.optionsList?.split(',')
    this.showAlways = !this.data.question.hidden && !this.data.question.displayCondition
    this.showOnCondition = !!this.data.question.displayCondition
  }

  private setTitles() {
    switch (this.data.type) {
      case QuestionType.FORMULA:
        this.title = 'Configure Calculated Answer'
        this.instructions = `1. Enter the question text and specify if answering is required.
          2. Label the answer field and assign a unique ID for tracking.
          3. Build the formula.`
        this.questionTextLabel = 'Enter description to display  (i.e: Number of doses)'
        this.configureTitle = 'Configure calculated answer field'
        break
      case QuestionType.DROPDOWN:
        this.title = `Configure Dropdown Answer`
        this.instructions = `1. Enter the question text and specify if answering is required.
          2. Label the answer field and assign a unique ID for tracking.`
        this.questionTextLabel = 'Enter question to display'
        break
      default:
        this.title = `Configure Question with ${
          this.data.type?.charAt(0) + this.data.type?.substring(1).toLowerCase()
        } Answer`
        this.instructions = `1. Enter the question text and specify if answering is required.
          2. Label the answer field and assign a unique ID for tracking.`
        this.questionTextLabel = 'Enter question to display'
    }
  }
}
